import {
  DEFAULT_INTERNAL_TRANSPORT_BASE_URL,
  type TransportBatchConfig,
  type TransportInternalSenderConfig,
  type TransportExternalSenderConfig,
} from '@/config';

export type ResolvedHttpSenderConfig = {
  readonly mode: 'internal' | 'external';
  readonly eventEndpoint: string;
  readonly batchEndpoint?: string;
  readonly batchFallback: 'single' | 'error';
  readonly headers: Readonly<Record<string, string>>;
  readonly timeoutMs: number;
};

const INTERNAL_EVENT_PATH = '/pixels/public/event';
const INTERNAL_BATCH_PATH = '/pixels/public/event/batch';

export function resolveInternalSenderConfig(
  config: TransportInternalSenderConfig,
  timeoutMs: number
): ResolvedHttpSenderConfig {
  const baseUrl = normalizeHttpUrl(
    config.baseUrl?.trim() || DEFAULT_INTERNAL_TRANSPORT_BASE_URL,
    'transport.sender.baseUrl'
  ).replace(/\/+$/, '');

  return {
    mode: 'internal',
    eventEndpoint: `${baseUrl}${INTERNAL_EVENT_PATH}`,
    batchEndpoint: `${baseUrl}${INTERNAL_BATCH_PATH}`,
    batchFallback: 'error',
    headers: {},
    timeoutMs,
  };
}

export function resolveExternalSenderConfig(
  config: TransportExternalSenderConfig,
  batch: TransportBatchConfig,
  timeoutMs: number
): ResolvedHttpSenderConfig {
  const eventEndpoint = normalizeHttpUrl(
    config.endpoints.event,
    'transport.sender.endpoints.event'
  );
  const batchEndpoint = config.endpoints.batch
    ? normalizeHttpUrl(
        config.endpoints.batch,
        'transport.sender.endpoints.batch'
      )
    : undefined;
  const batchFallback = config.batchFallback ?? 'single';

  if (batch.enabled && !batchEndpoint && batchFallback === 'error') {
    throw new Error(
      '[Transport] Batch is enabled but transport.sender.endpoints.batch is missing. Provide endpoints.batch, disable batch, or use batchFallback: "single".'
    );
  }

  return {
    mode: 'external',
    eventEndpoint,
    batchEndpoint,
    batchFallback,
    headers: sanitizeExternalHeaders(config.headers),
    timeoutMs,
  };
}

function normalizeHttpUrl(value: string, field: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`[Transport] ${field} must not be empty.`);

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error(`[Transport] ${field} must be an absolute HTTP(S) URL.`);
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error(`[Transport] ${field} must use HTTP or HTTPS.`);
  }

  return trimmed;
}

function sanitizeExternalHeaders(
  headers: Readonly<Record<string, string>> | undefined
): Readonly<Record<string, string>> {
  if (!headers) return {};

  const sanitized: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    const normalized = name.toLowerCase();
    if (normalized === 'content-type' || normalized === 'content-encoding') {
      continue;
    }
    sanitized[name] = value;
  }
  return sanitized;
}
